import KeyboardArrowDown from '@mui/icons-material/KeyboardArrowDown'
import { Box, InputBase, MenuItem, Select, SelectChangeEvent, Stack, Typography } from '@mui/material'
import { memo, useCallback, useMemo } from 'react'
import { TransactionType } from '../../../../proto/generated/order'
import { QuoteType } from '../../../../proto/generated/quick-trade'
import { OrderBook } from '../../../../proto/generated/security'
import {
    computeDisplaySecondaryFromPrimary,
    inputStepForQuantityType,
    QuickTradeQuantityType,
    roundPrimaryInputOnBlur,
} from '../../../../util/quickTradeQuantity'
import { computeQuickTradeEntryPriceDisplay, QuickTradeQuoteScalars } from '../../../../util/quickTradeQuote'
import { fieldArrowSx, fieldShellSx, typeLabelTypographySx, typeSelectSx, typeSelectWrapSx } from './quickTradeFieldChrome'

export { QuickTradeQuantityType }

function quantityTypeLabel(type: QuickTradeQuantityType): string {
    return type === QuickTradeQuantityType.QUANTITY ? 'Qty' : type
}

function formatUnitCount(value: number): string {
    if (Number.isInteger(value)) return String(value)
    return parseFloat(value.toFixed(2)).toString()
}

function formatSecondary(
    value: number | undefined,
    quantityType: QuickTradeQuantityType,
    foQuantityMode: boolean
): string {
    const zeroLabel =
        foQuantityMode && quantityType === QuickTradeQuantityType.AMOUNT ? '0 lots' : '0 units'
    if (value == null || !Number.isFinite(value) || value < 0) return zeroLabel
    if (value === 0) return zeroLabel
    const display = formatUnitCount(value)
    const n = Number(display)
    if (foQuantityMode && quantityType === QuickTradeQuantityType.AMOUNT) {
        return `${display} ${Math.abs(n) === 1 ? 'lot' : 'lots'}`
    }
    return `${display} ${Math.abs(n) === 1 ? 'unit' : 'units'}`
}

export type QuickTradeQuantityFieldProps = {
    quantityType: QuickTradeQuantityType
    value: number | '' | undefined
    onQuantityTypeChange: (type: QuickTradeQuantityType) => void
    onValueChange: (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => void
    showLots: boolean
    showQuantity: boolean
    showAmount: boolean
    lotSize: number
    quote: QuickTradeQuoteScalars
    orderBook?: OrderBook
    priceMultiplier: number
    tickSize: number
    quoteType?: QuoteType
    quoteValue?: number
    transactionType: TransactionType
    menuZIndex?: number
    /** F&O: amount RHS shows lots; lots RHS shows units. */
    foQuantityMode?: boolean
    error?: boolean
    helperText?: string
}

const QuickTradeQuantityField: React.FC<QuickTradeQuantityFieldProps> = ({
    quantityType,
    value,
    onQuantityTypeChange,
    onValueChange,
    showLots,
    showQuantity,
    showAmount,
    lotSize,
    quote,
    orderBook,
    priceMultiplier,
    tickSize,
    quoteType,
    quoteValue,
    transactionType,
    menuZIndex = 13000,
    foQuantityMode = false,
    error = false,
    helperText,
}) => {
    const unitPriceDisplay = useMemo(
        () =>
            computeQuickTradeEntryPriceDisplay({
                quote,
                orderBook,
                transactionType,
                quoteType,
                quoteValue,
                tickSize,
                priceMultiplier,
            }),
        [quote, orderBook, transactionType, quoteType, quoteValue, tickSize, priceMultiplier]
    )

    const primaryNum = value === '' || value == null ? undefined : Number(value)
    const secondary = useMemo(
        () =>
            primaryNum != null && primaryNum >= 0
                ? computeDisplaySecondaryFromPrimary(
                      quantityType,
                      primaryNum,
                      lotSize,
                      unitPriceDisplay,
                      foQuantityMode
                  )
                : undefined,
        [quantityType, primaryNum, lotSize, unitPriceDisplay, foQuantityMode]
    )

    const inputStep = inputStepForQuantityType(quantityType, lotSize)

    const handleBlur = useCallback(() => {
        if (primaryNum == null || Number.isNaN(primaryNum)) return
        const rounded = roundPrimaryInputOnBlur(quantityType, primaryNum, lotSize)
        if (rounded !== primaryNum) {
            onValueChange({ target: { value: String(rounded) } } as React.ChangeEvent<HTMLInputElement>)
        }
    }, [primaryNum, quantityType, lotSize, onValueChange])

    const typeOptions = useMemo(() => {
        const opts: QuickTradeQuantityType[] = []
        if (showLots) opts.push(QuickTradeQuantityType.LOTS)
        if (showQuantity) opts.push(QuickTradeQuantityType.QUANTITY)
        if (showAmount) opts.push(QuickTradeQuantityType.AMOUNT)
        return opts
    }, [showLots, showQuantity, showAmount])

    return (
        <Stack spacing={0} sx={{ width: '100%' }}>
            <Box sx={typeSelectWrapSx}>
                <Select<QuickTradeQuantityType>
                    value={quantityType}
                    variant="standard"
                    disableUnderline
                    onChange={(e: SelectChangeEvent<QuickTradeQuantityType>) =>
                        onQuantityTypeChange(e.target.value as QuickTradeQuantityType)
                    }
                    MenuProps={{ sx: { zIndex: menuZIndex } }}
                    sx={typeSelectSx}
                    renderValue={(selected) => (
                        <Box
                            component="span"
                            sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.25, color: 'text.secondary' }}
                        >
                            <Typography
                                component="span"
                                variant="caption"
                                sx={typeLabelTypographySx}
                            >
                                {quantityTypeLabel(selected)}
                            </Typography>
                            <KeyboardArrowDown sx={{ fontSize: 16, color: 'text.secondary' }} />
                        </Box>
                    )}
                >
                    {typeOptions.map((opt) => (
                        <MenuItem key={opt} value={opt} dense>
                            {quantityTypeLabel(opt)}
                        </MenuItem>
                    ))}
                </Select>
            </Box>
            <Box
                sx={{
                    ...fieldShellSx,
                    ...(error
                        ? {
                              borderColor: 'error.main',
                              '&:focus-within': { borderColor: 'error.main' },
                          }
                        : {}),
                }}
            >
                <InputBase
                    type="number"
                    value={value ?? ''}
                    onChange={onValueChange}
                    onBlur={handleBlur}
                    inputProps={{ min: 0, step: inputStep }}
                    sx={{
                        minWidth: 0,
                        fontSize: '0.875rem',
                        fontVariantNumeric: 'tabular-nums',
                        '& input[type=number]': { MozAppearance: 'textfield' },
                        '& input[type=number]::-webkit-outer-spin-button, & input[type=number]::-webkit-inner-spin-button': {
                            WebkitAppearance: 'none',
                            margin: 0,
                        },
                    }}
                />
                <Typography component="span" sx={fieldArrowSx}>
                    →
                </Typography>
                <Typography
                    variant="body2"
                    color="text.secondary"
                    sx={{ textAlign: 'right', fontVariantNumeric: 'tabular-nums', minWidth: 0 }}
                >
                    {formatSecondary(secondary, quantityType, foQuantityMode)}
                </Typography>
            </Box>
            {helperText ? (
                <Typography variant="caption" color="error" sx={{ mt: 0.5, lineHeight: 1.2 }}>
                    {helperText}
                </Typography>
            ) : null}
        </Stack>
    )
}

export default memo(QuickTradeQuantityField)
